import React from 'react'
import PropTypes from 'prop-types'

import { ProfileBG } from './ProfileBG'
import { ProfileRoles } from './ProfileRoles'
import { ProfileImage } from './ProfileImage'
import { ProfileUpdate } from './ProfileUpdate'

/**
 * Profile Component
 *
 * A React component that represents a user's profile page. It includes sections for profile background, profile image, roles, and updates.
 *
 * @module profile/Profile
 * @description A React component that represents a user's profile page. It includes sections for profile background, profile image, roles, and updates.
 * @example
 * const authDetails = { roleId: 'role123', session: { user: { userId: 'user123' } } }
 * const routeData = { apiEndPointSchema: JSON.stringify({ api: '/api/endpoint' }) }
 * const isLocalEnvironment = 'http://localhost'
 * const envData = { ... }
 *
 * return (
 *   <Profile
 *     authDetails={authDetails}
 *     routeData={routeData}
 *     isLocalEnvironment={isLocalEnvironment}
 *     envData={envData}
 *   />
 * )
 *
 * @param {Object} authDetails - Authentication details including role ID and user session.
 * @param {Object} routeData - Data related to API endpoints and schemas.
 * @param {string} isLocalEnvironment - URL or flag indicating whether the environment is local.
 * @param {Object} envData - Environment-related data used by the component.
 *
 * @returns {React.Element} The rendered component.
 */
export const Profile = ({ authDetails, routeData, isLocalEnvironment, envData }) => {
  return (
    <>
      <ProfileBG />
      <div className="trtui-row">
        <div className="trtui-col-xxl-3">
          <ProfileImage authDetails={authDetails} />
          <ProfileRoles authDetails={authDetails} />
        </div>
        <div className="trtui-col-xxl-9">
          <ProfileUpdate
            authDetails={authDetails}
            moduleSchema={JSON.parse(routeData.apiEndPointSchema)}
            isLocalEnvironment={isLocalEnvironment}
            envData={envData}
          />
        </div>
      </div>
    </>
  )
}

Profile.propTypes = {
  authDetails: PropTypes.object.isRequired,
  routeData: PropTypes.object.isRequired,
  isLocalEnvironment: PropTypes.string.isRequired,
  envData: PropTypes.object.isRequired,
}
